import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp, API } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, UsersRound, Trash2, MailCheck, Link2, Copy, Power, PowerOff, MapPin, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { ROLE_COLOR, SPECIALTIES, SERVICES, SERVICE_LABEL } from "@/lib/labels";

export default function Team() {
  const { t, user } = useApp();
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    email: "", name: "", password: "", role: "secretary", specialty: "", service: "general",
    commune: "", quartier: "", avenue: "", ville: "Kinshasa", pays: "RDC",
  });
  const [useEmail, setUseEmail] = useState(true);
  const [showAddress, setShowAddress] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  const load = async () => {
    const r = await axios.get(`${API}/staff`);
    setStaff(r.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!useEmail && form.password.length < 8) { toast.error("Mot de passe min 8 car."); return; }
    try {
      const addressFields = {
        commune: form.commune || "", quartier: form.quartier || "",
        avenue: form.avenue || "", ville: form.ville || "", pays: form.pays || "",
      };
      const payload = useEmail
        ? { email: form.email, name: form.name, role: form.role, specialty: form.specialty, service: form.service, ...addressFields }
        : { ...form, ...addressFields };
      const r = await axios.post(`${API}/staff/invite`, payload);
      if (useEmail) {
        setInviteResult(r.data);
        if (r.data.delivered) {
          toast.success(`Invitation envoyée à ${form.email}`);
        } else {
          toast.info("Invitation créée — partagez le lien manuellement");
        }
      } else {
        toast.success("Membre ajouté avec mot de passe direct");
        setOpen(false);
        setForm({ email: "", name: "", password: "", role: "secretary", specialty: "", service: "general", commune: "", quartier: "", avenue: "", ville: "Kinshasa", pays: "RDC" });
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const closeAndReset = () => {
    setOpen(false);
    setForm({ email: "", name: "", password: "", role: "secretary", specialty: "", service: "general", commune: "", quartier: "", avenue: "", ville: "Kinshasa", pays: "RDC" });
    setInviteResult(null);
    setUseEmail(true);
    setShowAddress(false);
  };

  const copyInviteUrl = () => {
    if (!inviteResult?.invite_url) return;
    navigator.clipboard.writeText(inviteResult.invite_url);
    toast.success("Lien copié");
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer ce membre ?")) return;
    try {
      await axios.delete(`${API}/staff/${id}`);
      toast.success("Supprimé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const toggleStatus = async (s) => {
    const action = s.active === false ? "réactiver" : "désactiver";
    if (!window.confirm(`${action.charAt(0).toUpperCase()+action.slice(1)} ${s.name} ?`)) return;
    try {
      await axios.patch(`${API}/users/${s.id}/status?active=${s.active === false}`);
      toast.success(`Compte ${action}é`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const roleLabel = (r) => t.roles[r] || r;
  const roleColor = (r) => ROLE_COLOR[r] || "#5C6B61";

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-[Outfit] font-bold">{t.dash.team}</h1>
        <Dialog open={open} onOpenChange={(v)=>{ if (!v) closeAndReset(); else setOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1E4631] hover:bg-[#163424] text-white" data-testid="invite-btn">
              <Plus className="w-4 h-4 mr-2"/> {t.dash.invite_staff}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t.dash.invite_staff}</DialogTitle></DialogHeader>
            {inviteResult ? (
              <div className="space-y-4" data-testid="invite-result">
                {inviteResult.delivered ? (
                  <div className="p-5 rounded-xl bg-[#2E7D32]/10 border border-[#2E7D32]/30 text-sm">
                    <div className="flex items-center gap-2 font-semibold text-[#2E7D32] mb-1">
                      <MailCheck className="w-4 h-4"/> Email envoyé !
                    </div>
                    <p className="text-[#5C6B61]">{inviteResult.message} Le destinataire recevra un lien pour activer son compte et définir son mot de passe (valide 7 jours).</p>
                  </div>
                ) : (
                  <div className="p-5 rounded-xl bg-[#E3A72F]/10 border border-[#E3A72F]/30 text-sm">
                    <div className="flex items-center gap-2 font-semibold text-[#1A1D1A] mb-2">
                      <Link2 className="w-4 h-4 text-[#E3A72F]"/> Email non envoyé — partagez le lien manuellement
                    </div>
                    <p className="text-[#5C6B61] mb-3">{inviteResult.message}</p>
                    {inviteResult.invite_url && (
                      <div className="flex gap-2">
                        <Input value={inviteResult.invite_url} readOnly className="font-mono text-xs" data-testid="invite-url"/>
                        <Button type="button" variant="outline" size="sm" onClick={copyInviteUrl} data-testid="invite-copy">
                          <Copy className="w-4 h-4"/>
                        </Button>
                      </div>
                    )}
                  </div>
                )}
                <DialogFooter>
                  <Button onClick={closeAndReset} className="bg-[#1E4631] hover:bg-[#163424]" data-testid="invite-close">Fermer</Button>
                </DialogFooter>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
                <div><Label>{t.dash.name}</Label><Input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="invite-name"/></div>
                <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} data-testid="invite-email"/></div>
                <div>
                  <Label>{t.dash.role}</Label>
                  <Select value={form.role} onValueChange={(v)=>setForm({...form, role: v})}>
                    <SelectTrigger data-testid="invite-role"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hospital_admin">{t.dash.admin_role}</SelectItem>
                      <SelectItem value="doctor">{t.dash.doctor_role}</SelectItem>
                      <SelectItem value="nurse">{t.dash.nurse_role}</SelectItem>
                      <SelectItem value="caregiver">{t.dash.caregiver_role}</SelectItem>
                      <SelectItem value="secretary">{t.dash.secretary_role}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.role === "doctor" && (
                  <div>
                    <Label>{t.dash.specialty}</Label>
                    <Select value={form.specialty} onValueChange={(v)=>setForm({...form, specialty: v})}>
                      <SelectTrigger data-testid="invite-specialty"><SelectValue placeholder="Choisir une spécialité"/></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {SPECIALTIES.map((sp) => <SelectItem key={sp} value={sp}>{sp}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {form.role !== "hospital_admin" && (
                  <div>
                    <Label>{t.dash.service}</Label>
                    <Select value={form.service} onValueChange={(v)=>setForm({...form, service: v})}>
                      <SelectTrigger data-testid="invite-service"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {SERVICES.map((sv) => <SelectItem key={sv.id} value={sv.id}>{sv.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="flex items-start gap-2 p-3 bg-[#1E4631]/5 rounded-lg border border-[#1E4631]/10">
                  <Checkbox id="useEmail" checked={useEmail} onCheckedChange={(v)=>setUseEmail(!!v)} data-testid="invite-mode-toggle"/>
                  <label htmlFor="useEmail" className="text-sm cursor-pointer leading-relaxed">
                    <strong>Envoyer une invitation par email</strong>
                    <div className="text-xs text-[#5C6B61] mt-0.5">Le membre recevra un lien pour activer son compte et choisir son mot de passe (recommandé).</div>
                  </label>
                </div>

                {!useEmail && (
                  <div>
                    <Label>Mot de passe (8+ car.)</Label>
                    <Input type="password" required={!useEmail} minLength={8} value={form.password}
                           onChange={(e)=>setForm({...form, password: e.target.value})} data-testid="invite-pw"/>
                    <div className="text-xs text-[#5C6B61] mt-1">Le membre devra changer ce mot de passe lors de sa première connexion.</div>
                  </div>
                )}

                {/* Adresse pliable */}
                <div className="border border-[#E5EBE7] rounded-lg overflow-hidden">
                  <button type="button" onClick={()=>setShowAddress(v=>!v)}
                          className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium hover:bg-[#FDFBF7] transition"
                          data-testid="invite-toggle-address">
                    <span className="inline-flex items-center gap-2 text-[#1A1D1A]">
                      <MapPin className="w-4 h-4 text-[#D96C4A]"/>
                      Adresse physique <span className="text-[#5C6B61] font-normal">(optionnel)</span>
                    </span>
                    {showAddress ? <ChevronUp className="w-4 h-4 text-[#5C6B61]"/> : <ChevronDown className="w-4 h-4 text-[#5C6B61]"/>}
                  </button>
                  {showAddress && (
                    <div className="p-4 grid grid-cols-2 gap-3 border-t border-[#E5EBE7] bg-[#FDFBF7]/40" data-testid="invite-address-fields">
                      <div><Label className="text-xs">Commune</Label><Input value={form.commune} onChange={(e)=>setForm({...form, commune: e.target.value})} placeholder="Gombe, Kalamu…" data-testid="invite-commune"/></div>
                      <div><Label className="text-xs">Quartier</Label><Input value={form.quartier} onChange={(e)=>setForm({...form, quartier: e.target.value})} placeholder="Lemba Q.7" data-testid="invite-quartier"/></div>
                      <div className="col-span-2"><Label className="text-xs">Avenue et N°</Label><Input value={form.avenue} onChange={(e)=>setForm({...form, avenue: e.target.value})} placeholder="Av. Kasa-Vubu N°123" data-testid="invite-avenue"/></div>
                      <div><Label className="text-xs">Ville</Label><Input value={form.ville} onChange={(e)=>setForm({...form, ville: e.target.value})} data-testid="invite-ville"/></div>
                      <div><Label className="text-xs">Pays</Label><Input value={form.pays} onChange={(e)=>setForm({...form, pays: e.target.value})} data-testid="invite-pays"/></div>
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={closeAndReset}>{t.dash.cancel}</Button>
                  <Button type="submit" className="bg-[#1E4631]" data-testid="invite-save">
                    {useEmail ? "Envoyer l'invitation" : t.dash.save}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-xl border border-[#E5EBE7] divide-y divide-[#E5EBE7]">
        {staff.length === 0 ? (
          <div className="p-12 text-center text-[#5C6B61]" data-testid="empty-team">Aucun membre</div>
        ) : staff.map((s) => {
          const isActive = s.active !== false;
          return (
          <div key={s.id} className={`p-5 flex items-center justify-between hover:bg-[#FDFBF7]/50 ${!isActive ? "opacity-60" : ""}`} data-testid={`staff-${s.id}`}>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full grid place-items-center" style={{ background: `${roleColor(s.role)}15`, color: roleColor(s.role) }}>
                <UsersRound className="w-5 h-5"/>
              </div>
              <div>
                <div className="font-semibold">{s.name}</div>
                <div className="text-xs text-[#5C6B61]">{s.email}</div>
                {(s.specialty || (s.service && s.service !== "general")) && (
                  <div className="text-[11px] text-[#1E4631] mt-0.5" data-testid={`staff-meta-${s.id}`}>
                    {[s.specialty, SERVICE_LABEL[s.service]].filter(Boolean).join(" · ")}
                  </div>
                )}
                {(s.commune || s.quartier || s.avenue) && (
                  <div className="text-[10px] text-[#5C6B61] mt-0.5 inline-flex items-center gap-1">
                    <MapPin className="w-2.5 h-2.5"/>
                    {[s.avenue, s.quartier, s.commune, s.ville].filter(Boolean).join(", ")}
                  </div>
                )}
              </div>
              <span className="ml-3 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold" style={{ background: `${roleColor(s.role)}15`, color: roleColor(s.role) }}>
                {roleLabel(s.role)}
              </span>
              {!isActive && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold bg-[#C62828]/10 text-[#C62828]">
                  Désactivé
                </span>
              )}
            </div>
            {s.id !== user.id && s.role !== "hospital_admin" && (
              <div className="flex gap-1">
                <button onClick={()=>toggleStatus(s)}
                        className={`p-2 rounded transition ${isActive ? "text-[#E3A72F] hover:bg-[#E3A72F]/10" : "text-[#2E7D32] hover:bg-[#2E7D32]/10"}`}
                        title={isActive ? "Désactiver" : "Réactiver"}
                        data-testid={`staff-toggle-${s.id}`}>
                  {isActive ? <PowerOff className="w-4 h-4"/> : <Power className="w-4 h-4"/>}
                </button>
                <button onClick={()=>onDelete(s.id)} className="text-[#C62828] hover:bg-red-50 p-2 rounded transition" data-testid={`staff-del-${s.id}`}>
                  <Trash2 className="w-4 h-4"/>
                </button>
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}
